import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import lodashValues from 'lodash/fp/values';
import moment from 'moment';

import Navbar from 'client/components/Navbar';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import H1 from 'client/components/H1';
import SrDatePicker from 'client/components/SrDatePicker';
import SrCalendarTimeRow from 'client/components/SrCalendarTimeRow';

import {
  SR_API_ACTION_ADMIN_ADD_TOUR_DATES_UNAVAILABLE,
  SR_API_ACTION_ADMIN_GET_ALL_TOUR_DATES_UNAVAILABLE,
  SR_API_ACTION_ADMIN_REMOVE_TOUR_DATE_UNAVAILABLE,
} from 'api/actionTypes';
import { SCREEN_ID_ADMIN_TOUR_TIME_BLOCK_OFF } from 'client/constants/screenIds';
import { USER_ROLE_ADMIN } from 'common/userRoles';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import allowedRoles from 'client/helpers/allowedRoles';

import MarketActions from 'client/actions/Market';
import branch from 'client/core/branch';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import postWithState from 'client/actions/postWithState';
import {
  getRangesOfTimes,
} from 'common/tourHelpers';

const initialState = { date: '', selectedTimes: [], marketId: '' };

function sortTourDatesUnavailable(dates = []) {
  return dates.sort((tourDateA, tourDateB) => {
    return momentWithAustinTimezone(tourDateA).diff(tourDateB) > 0 ? -1 : 1;
  });
}

function formatTourDatesUnavailable(tourDatesUnavailable = []) {
  return tourDatesUnavailable
    .reduce((memo, tourDate) => {
      const tourDatesUnavailable = momentWithAustinTimezone(tourDate);

      if (!memo[tourDatesUnavailable.format('YYYY-MM-DD')]) {
        memo[tourDatesUnavailable.format('YYYY-MM-DD')] = [];
      }

      memo[tourDatesUnavailable.format('YYYY-MM-DD')].push(tourDatesUnavailable.format('HH:mm'));

      return memo;
    }, {});
}

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  addTourDateApiState: {
    action: SR_API_ACTION_ADMIN_ADD_TOUR_DATES_UNAVAILABLE,
  },
})
@withScreenId(SCREEN_ID_ADMIN_TOUR_TIME_BLOCK_OFF)
@branch({
  marketsById: ['markets', 'byId'],
})
class AdminTourTimesPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin Tour Times | Sunroom Rentals',
    section: 'Admin',
    bodyClasses: 'page-admin',
  });

  static fetchData(match, inject) {
    return Promise.all([
      MarketActions.getList(inject),
    ]);
  }

  static propTypes = {
    marketsById: PropTypes.object.isRequired,
    addTourDateApiState: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      tourDatesUnavailableByMarket: {},
    };
  }

  componentDidMount() {
    this._getTourDates();
  }

  _onSubmit = (values, { resetForm }) => {
    const date = momentWithAustinTimezone(values.date);
    const tourDates = values.selectedTimes.map((time) => {
      return momentWithAustinTimezone(
        `${date.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm'
      ).toISOString();
    });

    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_ADD_TOUR_DATES_UNAVAILABLE,
      payload: {
        tourDates: tourDates,
        marketId: values.marketId,
      },
    }).then(() => {
      resetForm({ ...initialState });
      this._getTourDates();
    });
  }

  _getTourDates = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_GET_ALL_TOUR_DATES_UNAVAILABLE,
    }).then((tourDatesUnavailableByMarket) => {
      if (!tourDatesUnavailableByMarket) return;
      this.setState({ tourDatesUnavailableByMarket });
    });
  }

  _removeTourDate = (marketId, tourDate) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REMOVE_TOUR_DATE_UNAVAILABLE,
      payload: {
        marketId: marketId,
        tourDate: tourDate,
      },
    }).then(() => this._getTourDates());
  }

  render() {
    const { addTourDateApiState, marketsById } = this.props;
    const { tourDatesUnavailableByMarket } = this.state;
    const marketList = lodashValues(marketsById);

    return (
      <div className='adminContainer'>
        <Navbar redirectLogoTo='/admin' />

        <div className='section'>
          <H1>Managed tour times</H1>

          <Formik
            initialValues={{ ...initialState }}
            onSubmit={this._onSubmit}
            render={({ isValid, values, handleSubmit, setFieldValue }) => {
              let dateDOM = null;

              if (values.date && values.marketId) {
                const date = momentWithAustinTimezone(values.date);
                const tourDatesUnavailable = (
                  formatTourDatesUnavailable(tourDatesUnavailableByMarket[values.marketId])[date.format('YYYY-MM-DD')] // eslint-disable-line max-len
                  || []
                );

                const rangeOfTimes = getRangesOfTimes({
                  date,
                });

                dateDOM = (
                  <div className='choiceContainer'>
                    {rangeOfTimes.map(
                      ({ hour, minute, format }) => (
                        <SrCalendarTimeRow
                          key={`${hour}:${minute}`}
                          hour={hour}
                          minute={minute}
                          disabled={tourDatesUnavailable.includes(format)}
                          selected={values.selectedTimes.includes(format)}
                          onClick={() => {
                            const { selectedTimes } = values;
                            let newTimes = null;

                            if (selectedTimes.includes(format)) {
                              newTimes = selectedTimes.filter((time) => time !== format);
                            }
                            else {
                              newTimes = [...selectedTimes, format];
                            }

                            setFieldValue(
                              'selectedTimes',
                              newTimes
                            );
                          }}
                        />
                      )
                    )}
                  </div>
                );
              }

              return (
                <Form>
                  <Field
                    component='select'
                    name='marketId'
                    className='formik-select'
                    onChange={(e) => {
                      setFieldValue('marketId', e.target.value);
                    }}
                  >
                    <option
                      label='Choose ..'
                      value=''
                    />
                    {marketList.map((choice) => (
                      <option
                        label={choice.displayName}
                        value={choice.id}
                        key={choice.id}
                      >
                        {choice.displayName}
                      </option>
                    ))}
                  </Field>
                  <SrDatePicker
                    placeholder='date'
                    onChange={(newValue) => {
                      const momentInstance = momentWithAustinTimezone(newValue);
                      setFieldValue('date', momentInstance.isValid() ? momentInstance.toISOString() : null);
                      setFieldValue('selectedTimes', []);
                    }}
                    isValidDate={(date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days'))}
                    closeOnSelect
                    dateFormat='MM/DD/YYYY'
                  />

                  {dateDOM}
                  <ButtonDeprecated
                    disabled={
                      !isValid
                      || !values.selectedTimes.length
                      || !values.marketId
                      || addTourDateApiState.inProgress
                    }
                    onClick={handleSubmit}
                  >
                    mark time as not available
                  </ButtonDeprecated>
                </Form>
              );
            }}
          />

          {marketList.map((market) => (
            <div key={market.id}>
              <span>{market.displayName}</span>
              <div>
                {sortTourDatesUnavailable(tourDatesUnavailableByMarket[market.id]).map((tourDate) => {
                  const date = moment(tourDate);
                  return (
                    <div key={tourDate} style={{ margin: 10 }}>
                      <span>
                        {date.hours() === 0 ? date.format('YYYY-MM-DD') : date.format('YYYY-MM-DD hh:mm A')}
                      </span>
                      <span
                        onClick={() => this._removeTourDate(market.id, tourDate)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span
                          role='img'
                          aria-label='delete-listing-icon'
                        >
                          🙅‍
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

        </div>
      </div>
    );
  }
}

AdminTourTimesPage.displayName = 'AdminTourTimesPage';

export default AdminTourTimesPage;
